import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { useUserPurchases } from '@/hooks/useUserPurchases';
import { signCartData, verifyCartData, sanitizeCartItem } from '@/lib/cartSecurity';

export interface CartItem {
  id: string;
  type: 'document' | 'course';
  title: string;
  price: number;
  currency: string;
  description?: string;
  author_id?: string;
  instructor_id?: string;
  image_url?: string;
}

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] };

interface CartContextType {
  state: CartState;
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  isItemInCart: (id: string) => boolean;
  getCartTotal: () => number;
  isItemOwned: (id: string, type: 'document' | 'course') => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return state; // Item already in cart, don't add again
      }
      
      const newItems = [...state.items, action.payload];
      const total = newItems.reduce((sum, item) => sum + item.price, 0);
      
      return {
        items: newItems,
        total,
        itemCount: newItems.length,
      };
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      const total = newItems.reduce((sum, item) => sum + item.price, 0);
      
      return {
        items: newItems,
        total,
        itemCount: newItems.length,
      };
    }
    
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        itemCount: 0,
      };
    
    case 'LOAD_CART': {
      const total = action.payload.reduce((sum, item) => sum + item.price, 0);
      return {
        items: action.payload,
        total,
        itemCount: action.payload.length,
      };
    }
    
    default:
      return state;
  }
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0,
    itemCount: 0,
  });
  const { hasItem } = useUserPurchases();

  // Load cart from localStorage on mount with integrity verification
  useEffect(() => {
    const loadCart = async () => {
      const savedCart = localStorage.getItem('shopping-cart');
      if (savedCart) {
        try {
          // Try to verify signed data
          const verifiedItems = await verifyCartData(savedCart);
          
          if (verifiedItems) {
            // Sanitize items before loading
            const sanitizedItems = verifiedItems.map(sanitizeCartItem);
            dispatch({ type: 'LOAD_CART', payload: sanitizedItems });
          } else {
            // Signature verification failed or data corrupted
            console.warn('Cart integrity check failed - clearing cart');
            localStorage.removeItem('shopping-cart');
          }
        } catch (error) {
          // Fallback: try to load as plain JSON (legacy support)
          try {
            const items = JSON.parse(savedCart);
            const sanitizedItems = items.map(sanitizeCartItem);
            dispatch({ type: 'LOAD_CART', payload: sanitizedItems });
            
            // Re-sign the data for future loads
            const signedData = await signCartData(sanitizedItems);
            localStorage.setItem('shopping-cart', signedData);
          } catch (parseError) {
            console.error('Error loading cart from localStorage:', parseError);
            localStorage.removeItem('shopping-cart');
          }
        }
      }
    };
    
    loadCart();
  }, []);

  // Save cart to localStorage with integrity signature whenever it changes
  useEffect(() => {
    const saveCart = async () => {
      if (state.items.length > 0) {
        const signedData = await signCartData(state.items);
        localStorage.setItem('shopping-cart', signedData);
      } else {
        localStorage.removeItem('shopping-cart');
      }
    };
    
    saveCart();
  }, [state.items]);

  const addItem = (item: CartItem) => {
    // Check if user already owns this item
    if (hasItem(item.id, item.type)) {
      toast({
        title: "Already owned",
        description: `You already own ${item.title}.`,
        variant: "destructive",
      });
      return;
    }

    // Sanitize item before adding to cart
    const sanitizedItem = sanitizeCartItem(item);
    
    dispatch({ type: 'ADD_ITEM', payload: sanitizedItem });
    toast({
      title: "Added to cart",
      description: `${item.title} has been added to your cart.`,
    });
  };

  const removeItem = (id: string) => {
    const item = state.items.find(item => item.id === id);
    dispatch({ type: 'REMOVE_ITEM', payload: id });
    if (item) {
      toast({
        title: "Removed from cart",
        description: `${item.title} has been removed from your cart.`,
      });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast({
      title: "Cart cleared",
      description: "All items have been removed from your cart.",
    });
  };

  const isItemInCart = (id: string) => {
    return state.items.some(item => item.id === id);
  };

  const getCartTotal = () => {
    return state.total;
  };

  const isItemOwned = (id: string, type: 'document' | 'course') => {
    return hasItem(id, type);
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addItem,
        removeItem,
        clearCart,
        isItemInCart,
        getCartTotal,
        isItemOwned,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}